import { EmailTemplatesService, QuestionnairesService, FormsService } from '@/types/generated'
import type { CustomerEmailTemplate, TAgentAction } from '@/types/generated'
import type { ActionFlowStrategy, ActionSummaryParts } from '@/composables/useAiAssistantWidget'

// Cache email templates across resolves
let cachedTemplates: CustomerEmailTemplate[] | null = null

const ensureTemplates = async (): Promise<CustomerEmailTemplate[]> => {
  if (cachedTemplates) return cachedTemplates
  const response = await EmailTemplatesService.emailTemplatesList({ limit: 1000 })
  cachedTemplates = (response.results as CustomerEmailTemplate[]) || []
  return cachedTemplates
}

interface EmailActionFlowCallbacks {
  /** Pre-fill the email form and scroll to it */
  onReview: (action: TAgentAction) => Promise<void> | void
  /** Send the email */
  onSend: (action: TAgentAction) => Promise<void>
  /** Format a questionnaire title for display (e.g. "der {name}-Fragebogen") */
  formatQuestionnaire: (title: string) => string
  /** Format a PDF form title for display (e.g. "das PDF-Formular {name}") */
  formatPdfForm: (title: string) => string
}

/**
 * Creates an ActionFlowStrategy for SEND_CLIENT_TEMPLATE_EMAIL actions.
 *
 * The summary resolution (API calls to resolve UUIDs → names) lives here.
 * The page only provides the thin callbacks that touch its own state.
 */
export function createEmailActionFlow(callbacks: EmailActionFlowCallbacks): ActionFlowStrategy {
  return {
    resolveSummary: async (action: TAgentAction): Promise<ActionSummaryParts | null> => {
      const payload = action.payload as any
      if (!payload?.template_uuid) return null

      const templates = await ensureTemplates()
      const template = templates.find((t) => t.uuid === payload.template_uuid)
      if (!template?.name) return null

      // Resolve attachment names in parallel
      const details: string[] = []
      const promises: Promise<void>[] = []

      if (payload.questionnaire_uuids?.length) {
        for (const uuid of payload.questionnaire_uuids as string[]) {
          promises.push(
            QuestionnairesService.questionnairesRetrieve({ uuid })
              .then((q) => {
                if (q.title) details.push(callbacks.formatQuestionnaire(q.title))
              })
              .catch(() => {})
          )
        }
      }
      if (payload.pdf_form_uuids?.length) {
        for (const id of payload.pdf_form_uuids as string[]) {
          promises.push(
            FormsService.formsRetrieve({ id })
              .then((f) => {
                if (f.title) details.push(callbacks.formatPdfForm(f.title))
              })
              .catch(() => {})
          )
        }
      }
      await Promise.all(promises)

      return { label: template.name, details }
    },

    onReview: (action) => callbacks.onReview(action),
    onSend: (action) => callbacks.onSend(action),
    backendCommits: true
  }
}
